import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { formatCostUsd } from "../services/queryExplainService";

function Metric({ label, value }) {
  return (
    <Box sx={{ minWidth: 120 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">{label}</Typography>
      <Typography variant="h6" fontWeight={800}>{value}</Typography>
    </Box>
  );
}

export default function QueryExplainDialog({ open, explain, onClose }) {
  const [copied, setCopied] = useState(false);

  if (!explain) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Explain Query</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info">Run a query first to see its execution plan.</Alert>
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
      </Dialog>
    );
  }

  const perf = explain.performance || {};
  const copyQuery = async () => {
    await navigator.clipboard.writeText(explain.firestoreQuery || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6" fontWeight={700}>Explain Query</Typography>
          <Chip size="small" color={explain.mode === "server" ? "success" : "default"} label={explain.mode === "server" ? "Server Query" : "Client Query"} />
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {explain.error && (
            <Alert severity="error">{explain.error.message || "Query failed"}</Alert>
          )}
          {explain.missingIndex && (
            <Alert
              severity="warning"
              action={
                explain.missingIndex.consoleUrl ? (
                  <Button color="inherit" size="small" href={explain.missingIndex.consoleUrl} target="_blank" rel="noreferrer">
                    Open Firebase Console
                  </Button>
                ) : null
              }
            >
              Composite index required (FAILED_PRECONDITION). Create the index, then re-run.
            </Alert>
          )}

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">Firestore Query</Typography>
              <Button size="small" startIcon={<ContentCopyIcon />} onClick={copyQuery}>
                {copied ? "Copied" : "Copy Query"}
              </Button>
            </Stack>
            <Typography
              component="pre"
              sx={{ fontSize: 12, bgcolor: "action.hover", p: 1.5, borderRadius: 1, overflow: "auto", mt: 0.5 }}
            >
              {explain.firestoreQuery}
            </Typography>
          </Box>

          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
            <Metric label="Docs Returned" value={explain.documentsReturned} />
            <Metric label="Estimated Reads" value={explain.estimatedReads} />
            <Metric label="Estimated Cost" value={formatCostUsd(explain.estimatedCostUsd)} />
            <Metric label="Client Refined" value={explain.clientRefined} />
          </Stack>

          <Box>
            <Typography variant="subtitle2" gutterBottom>Indexes Used</Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {explain.indexesUsed.map((idx) => (
                <Chip key={idx} size="small" variant="outlined" label={idx} />
              ))}
              {explain.needsComposite && <Chip size="small" color="warning" label="Composite index required" />}
            </Stack>
          </Box>

          {explain.warnings?.length > 0 && (
            <Alert severity="info">
              {explain.warnings.map((w, i) => <div key={i}>{w}</div>)}
            </Alert>
          )}

          <Divider />

          <Box>
            <Typography variant="subtitle2" gutterBottom>Performance Panel</Typography>
            <Table size="small">
              <TableBody>
                {[
                  ["Query Time", `${perf.queryTimeMs ?? 0} ms`],
                  ["Download Time", `${perf.downloadTimeMs ?? 0} ms`],
                  ["Processing Time", `${perf.processingTimeMs ?? 0} ms`],
                  ["Render Time", `${perf.renderTimeMs ?? 0} ms`],
                  ["Network / Latency", `${perf.networkTimeMs ?? 0} ms`],
                  ["Total Time", `${perf.totalTimeMs ?? 0} ms`],
                ].map(([k, v]) => (
                  <TableRow key={k}>
                    <TableCell sx={{ color: "text.secondary", width: 200 }}>{k}</TableCell>
                    <TableCell><strong>{v}</strong></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
